import { ChangeEvent, FormEvent, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { isAxiosError } from 'axios'
import { ArrowLeft, Download, FileSpreadsheet, Plus, Upload } from 'lucide-react'
import { Alerta, Badge, Boton, Campo, Entrada, Panel, Tabla } from '../../components/kit'
import { DialogoAgregarBienes } from '../../components/inventario/DialogoAgregarBienes'
import { useAplicarPlanilla, useDescargarErrores, useRevisarPlanilla } from '../../queries/useInventario'
import { FilaPlanilla, RevisionPlanilla } from '../../types/inventario'
import { mensajeDeError } from '../../utils/errores'
import { CAMPOS, valor } from '../../utils/inventario'

const ACCION: Record<FilaPlanilla['accion'], { texto: string; tono: 'exito' | 'info' | 'error' }> = {
  crear: { texto: 'Nuevo', tono: 'exito' },
  actualizar: { texto: 'Cambia', tono: 'info' },
  error: { texto: 'Error', tono: 'error' },
}
const MAX_FILAS_VISTAS = 300

/**
 * «Descripción: SILLA → SILLA GIRATORIA», «Traslado: N3-OF310 → P3-OF310». Si la oficina
 * se escribió a mano, cómo se entendió: «Oficina: «327» → N3-OF327 · OF. 327».
 */
function detalle(f: FilaPlanilla): string[] {
  // En un bien nuevo, la descripción ya está en la columna «Bien» y la etiqueta pendiente es lo normal.
  const obvio = (c: string, despues: unknown) => f.accion === 'crear' && (c === 'descripcion' || (c === 'etiqueta_pendiente' && despues === true))
  const lineas = Object.entries(f.cambios)
    .filter(([c, [, despues]]) => !(c === 'oficina' && f.oficina_leida) && !obvio(c, despues))
    .map(([c, [antes, despues]]) =>
      f.accion === 'crear' ? `${CAMPOS[c] ?? c}: ${valor(c, despues)}` : `${CAMPOS[c] ?? c}: ${valor(c, antes)} → ${valor(c, despues)}`,
    )
  if (f.oficina_leida) lineas.push(`Oficina: ${f.oficina_leida}`)
  else if (f.accion === 'crear' && !('oficina' in f.cambios)) lineas.push('Oficina: sin oficina')
  if (f.traslado) lineas.push(`Traslado: ${f.traslado.desde ?? 'sin oficina'} → ${f.traslado.hacia}`)
  return lineas
}

/**
 * Cargar y editar bienes en masa con una planilla Excel. Se descarga (para bienes nuevos,
 * con las filas pedidas por tipo; o desde Bienes, los bienes filtrados para editarlos),
 * se completa en el computador y se sube. Primero se ve qué haría cada fila y cómo se
 * entendió cada oficina; se aplica todo o nada. Con errores, se descarga la misma
 * planilla con una columna «Error», se corrige ahí y se vuelve a subir.
 */
export default function Planilla() {
  const revisar = useRevisarPlanilla()
  const aplicar = useAplicarPlanilla()
  const descargarErrores = useDescargarErrores()
  const [agregando, setAgregando] = useState(false)
  const [archivo, setArchivo] = useState<File | null>(null)
  const [revision, setRevision] = useState<RevisionPlanilla | null>(null)
  const [aplicada, setAplicada] = useState<RevisionPlanilla | null>(null)
  const entrada = useRef<HTMLInputElement>(null)

  const elegir = (e: ChangeEvent<HTMLInputElement>) => {
    setArchivo(e.target.files?.[0] ?? null)
    setRevision(null)
    setAplicada(null)
    descargarErrores.reset()
  }

  const subir = (e: FormEvent) => {
    e.preventDefault()
    if (archivo) revisar.mutate(archivo, { onSuccess: setRevision })
  }

  const confirmar = () => {
    if (!archivo) return
    aplicar.mutate(archivo, {
      onSuccess: (r) => {
        setAplicada(r)
        setRevision(null)
        setArchivo(null)
        if (entrada.current) entrada.current.value = ''
      },
      // Si algo cambió entre la vista previa y el «Aplicar», el servidor devuelve la revisión nueva.
      onError: (error) => {
        if (isAxiosError(error) && error.response?.data?.filas) setRevision(error.response.data as RevisionPlanilla)
      },
    })
  }

  const cambios = revision?.filas ?? []
  const conError = cambios.filter((f) => f.errores.length)
  const total = revision ? revision.crear + revision.actualizar : 0
  return (
    <div className="pila pila--amplia">
      <div>
        <Boton variante="text" tam="sm" icono={ArrowLeft} to="/app/bienes">Bienes</Boton>
      </div>
      <header className="pagina__cabecera">
        <h1 className="dcc-t-heading-m">Cargar o editar bienes con una planilla</h1>
        <p className="texto-secundario">
          Para muchos bienes a la vez: se completa en Excel y se sube. Nada se aplica sin antes ver qué va a cambiar.
        </p>
      </header>

      <div className="grilla">
        <Panel titulo="1. Agregar bienes">
          <div className="pila">
            <p>
              Indica cuántos inventariables, no inventariables y otros vas a agregar: se descarga una planilla con esas filas. La
              oficina se escribe como la conoces (327, BP-P-2-04) y puede quedar vacía.
            </p>
            <div>
              <Boton variante="outlined" icono={Plus} onClick={() => setAgregando(true)}>Agregar bienes</Boton>
            </div>
          </div>
        </Panel>
        <Panel titulo="1. O editar los que existen">
          <div className="pila">
            <p>
              En <Link to="/app/bienes">Bienes</Link>, filtra (por ejemplo, «Por revisar: sin ubicación») y usa «Descargar planilla». Edita
              y súbela aquí. Cambiar la oficina de un bien es un traslado.
            </p>
          </div>
        </Panel>
      </div>
      {agregando && <DialogoAgregarBienes enlaceCargar={false} onCerrar={() => setAgregando(false)} />}

      <Panel titulo="2. Subir la planilla">
        <form className="fila" onSubmit={subir}>
          <Campo etiqueta="Planilla (.xlsx)" ayuda="Hasta 5 MB y 5.000 filas.">
            <Entrada ref={entrada} type="file" accept=".xlsx" onChange={elegir} />
          </Campo>
          <Boton type="submit" icono={Upload} cargando={revisar.isPending} disabled={!archivo}>Revisar</Boton>
        </form>
        {revisar.isError && <Alerta tipo="error">{mensajeDeError(revisar.error)}</Alerta>}
      </Panel>

      {aplicada && (
        <Alerta tipo="success" titulo="Planilla aplicada">
          {aplicada.crear} bienes nuevos y {aplicada.actualizar} actualizados ({aplicada.traslados} traslados). Todo quedó en el historial.
        </Alerta>
      )}

      {revision && (
        <Panel
          titulo="3. Vista previa"
          acciones={
            <Boton icono={FileSpreadsheet} onClick={confirmar} cargando={aplicar.isPending} disabled={revision.errores > 0 || total === 0}>
              Aplicar {total} {total === 1 ? 'cambio' : 'cambios'}
            </Boton>
          }
        >
          <div className="pila">
            <section className="cifras" aria-label="Resumen de la planilla">
              <div className="cifra cifra--exito"><span className="cifra__rotulo">Bienes nuevos</span><b className="cifra__numero">{revision.crear}</b></div>
              <div className="cifra"><span className="cifra__rotulo">Se actualizan</span><b className="cifra__numero">{revision.actualizar}</b><span className="cifra__bajada">{revision.traslados} con traslado</span></div>
              <div className="cifra"><span className="cifra__rotulo">Sin cambios</span><b className="cifra__numero">{revision.sin_cambios}</b></div>
              <div className={`cifra${revision.errores ? ' cifra--error' : ''}`}><span className="cifra__rotulo">Con errores</span><b className="cifra__numero">{revision.errores}</b></div>
            </section>
            {revision.errores > 0 ? (
              <>
                <Alerta tipo="error" titulo="No se puede aplicar">
                  {revision.errores === 1 ? 'Corrige la fila con error' : `Corrige las ${revision.errores} filas con errores`} y vuelve a
                  subir la planilla: se aplica todo o nada. La planilla con los errores trae una columna «Error» que dice qué corregir en
                  cada fila.
                </Alerta>
                <div>
                  <Boton
                    variante="outlined"
                    icono={Download}
                    onClick={() => archivo && descargarErrores.mutate(archivo)}
                    cargando={descargarErrores.isPending}
                  >
                    Descargar la planilla con los errores
                  </Boton>
                </div>
                {descargarErrores.isError && <Alerta tipo="error">{mensajeDeError(descargarErrores.error)}</Alerta>}
              </>
            ) : total === 0 ? (
              <Alerta tipo="info">La planilla no cambia nada.</Alerta>
            ) : null}
            {(conError.length ? conError : cambios).length > 0 && (
              <Tabla etiqueta={conError.length ? 'Filas con errores' : 'Cambios'} apilable>
                <thead>
                  <tr>
                    <th scope="col">Fila</th>
                    <th scope="col">Bien</th>
                    <th scope="col">Qué pasa</th>
                    <th scope="col">Detalle</th>
                  </tr>
                </thead>
                <tbody>
                  {(conError.length ? conError : cambios).slice(0, MAX_FILAS_VISTAS).map((f) => (
                    <tr key={f.fila}>
                      <td className="tabla__principal">Fila {f.fila}</td>
                      <td data-etiqueta="Bien" className="tabla__texto">
                        <div>
                          {f.numero || 'Sin N°'}
                          <span className="tabla__sub">{f.descripcion || '—'}</span>
                        </div>
                      </td>
                      <td data-etiqueta="Qué pasa">
                        <Badge tono={ACCION[f.accion].tono}>{ACCION[f.accion].texto}</Badge>
                      </td>
                      <td data-etiqueta="Detalle" className="tabla__texto">
                        <div>
                          {(f.errores.length ? f.errores : detalle(f)).map((linea) => (
                            <span key={linea} className="tabla__linea">{linea}</span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Tabla>
            )}
            {(conError.length ? conError : cambios).length > MAX_FILAS_VISTAS && (
              <p className="texto-secundario">Se muestran las primeras {MAX_FILAS_VISTAS} filas.</p>
            )}
          </div>
        </Panel>
      )}
    </div>
  )
}
